import { next } from '@birdi/theme';
import { css, Interpolation, Theme } from '@emotion/react';
import React, { JSX } from 'react';
import { Spinner } from './spinner';

export type ButtonVariant = 'solid' | 'outline' | 'tertiary';
export type ButtonSize = 'regular' | 'small';

export interface BaseButtonProps {
  loading?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  hasIcon?: boolean;
}

export type ButtonProps = BaseButtonProps & JSX.IntrinsicElements['button'];

const variants: Record<ButtonVariant, Interpolation<Theme>> = {
  solid: next.buttons.primary,
  outline: next.buttons.secondary,
  tertiary: next.buttons.tertiary,
};

const sizes: Record<ButtonSize, Interpolation<Theme>> = {
  regular: css``,
  small: css`
    font-size: 0.875rem;
    height: 30px;
    padding: 0 14px;
  `,
};

const withIcon = css`
  gap: 8px;
  padding: 0 16px;
`;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      loading,
      children,
      variant = 'solid',
      size = 'regular',
      disabled,
      hasIcon,
      ...props
    },
    ref,
  ) => (
    <button
      css={[variants[variant], sizes[size], hasIcon && withIcon]}
      ref={ref}
      disabled={disabled}
      {...props}
    >
      {!loading ? (
        children
      ) : (
        <>
          <span
            css={css`
              display: contents;
              visibility: hidden;
            `}
          >
            {children}
          </span>
          <Spinner
            color="currentColor"
            css={css`
              position: absolute;
            `}
          />
        </>
      )}
    </button>
  ),
);

// TODO: probably have a common BaseButton component
export type AnchorButtonProps = BaseButtonProps & JSX.IntrinsicElements['a'];

export const AnchorButton = React.forwardRef<
  HTMLAnchorElement,
  AnchorButtonProps
>(
  (
    {
      loading,
      children,
      variant = 'solid',
      size = 'regular',
      disabled,
      hasIcon,
      ...props
    },
    ref,
  ) => (
    <a
      css={[variants[variant], sizes[size], hasIcon && withIcon]}
      ref={ref}
      {...props}
    >
      {!loading ? (
        children
      ) : (
        <>
          <span
            css={css`
              display: contents;
              visibility: hidden;
            `}
          >
            {children}
          </span>
          <Spinner
            color="currentColor"
            css={css`
              position: absolute;
            `}
          />
        </>
      )}
    </a>
  ),
);

export const IconButton = React.forwardRef<
  HTMLButtonElement,
  JSX.IntrinsicElements['button']
>((props, ref) => <button css={next.buttons.icon} ref={ref} {...props} />);
